import { browserDetect } from "./browser";
import { getIdToken } from "./firebase";

/**
 * Set on `<html>` by the extension's content script (chrome `content.ts`).
 *
 * 🪤 MUST stay above the module-level `isPluginInstalled()` call below. That
 * function is hoisted, so it can be CALLED before this line runs, but a `const`
 * cannot be READ before its declaration executes: declared lower down, local
 * dev crashed at load with "Cannot access 'PLUGIN_ATTRIBUTE' before
 * initialization" and rendered a blank page. Production hid it only because
 * `transform-remove-console` (craco.config.js) deletes that `console.info`
 * call, arguments included, so the read never happens there.
 */
export const PLUGIN_ATTRIBUTE = "equility-extension";

interface IAuthCredentials {
	token: string;
}

const PLUGIN_REFRESH_INTERVAL = 4000; // Check every 4 seconds to refresh plugin status

console.info("isPluginInstalled", isPluginInstalled());
console.info("isPluginNeedInstall", isPluginNeedInstall());

/** Browsers that can install the Equility extension from the Chrome Web Store. */
export function isPluginSupportedBrowser() {
	const allowed = ["chrome", "edge", "opera", "brave"];
	return allowed.includes(browserDetect());
}

export function isPluginNeedInstall() {
	if (isPluginSupportedBrowser()) {
		return !isPluginInstalled();
	}
	return false;
}

export function isPluginInstalled() {
	return !!document.documentElement.hasAttribute(PLUGIN_ATTRIBUTE);
}

function keepPluginAlive() {
	if (isPluginNeedInstall()) {
		console.warn("Plugin needs installation!");
		return;
	}

	// Set or update the last validation time for the plugin
	localStorage.setItem("plugin-last-validation", Date.now().toString());

	// Repeat the check to ensure plugin remains active
	setTimeout(keepPluginAlive, PLUGIN_REFRESH_INTERVAL);
}

// Start the plugin keep-alive mechanism
keepPluginAlive();

window.addEventListener(
	"message",
	(event: any) => {
		// We only accept messages from this window to itself [i.e. not from any iframes]
		if (event.source !== window) return;

		if (event.data.type && event.data.type === "GET_AUTH") {
			getIdToken().then((token) => {
				shareAuth(token ? { token } : null);
			});
		}
	},
	false,
);

export function shareAuth(auth: IAuthCredentials | null) {
	const data = { type: "FROM_PAGE_SETAUTH", auth };
	window.postMessage(data, "*");
}

export function runReviewSync(params: IRunReview) {
	const { from, to, companyId } = params;

	const daterange = from + "--" + to;
	const accountingMethod = params.method;
	const company = { qbo_id: params.realmId, id: companyId };
	const backurl = getReviewBackUrl(companyId, daterange);

	localStorage.setItem(
		"qbosync",
		JSON.stringify({ date: Date.now(), url: backurl, accountingMethod }),
	);
	localStorage.setItem(
		"compliance-" + companyId + "-" + daterange,
		accountingMethod,
	);

	const data = { type: "FROM_PAGE_TASK", company, backurl };
	window.postMessage(data, "*");
}

function getReviewBackUrl(companyId: number, daterange: string) {
	const { host, protocol } = window.location;
	const pathname = `/company/${companyId}/review/run?daterange=${daterange}`;
	return `${protocol}//${host}${pathname}`;
}

interface IRunReview {
	from: string;
	to: string;
	companyId: number;
	realmId: string;
	method: string;
}
